import { toast } from "react-toastify";
import { ApiService } from "../../../config";
import { handleError } from "../../../utils/parseApiReturn";
import { TUser } from "../../users/models";
import {
  IGoogleVerifyRecaptchaProps,
  ISignUpProps,
  TApiRes,
  TEmailExists,
  TLogin,
  TLoginSocial,
  TVerifyEmail
} from "../models";

const signUp = async ({
  email,
  password,
  type,
  doc,
  fullName,
  phone,
  ambassador,
  token
}: ISignUpProps): Promise<TApiRes> => {
  try {
    const params = {
      email,
      token,
      password,
      type,
      ambassador,
      fullName,
      phone,
      ...(type === "COMPANY" ? { cnpj: doc } : { cpf: doc })
    };
    const response = await ApiService.post(`/auth/signup`, params);

    return response.data;
  } catch (error: any) {
    return handleError(
      error,
      "Erro ao criar a conta, reinicie a página e tente novamente."
    );
  }
};

const googleVerifyReCaptcha = async ({
  token
}: IGoogleVerifyRecaptchaProps): Promise<boolean> => {
  try {
    const result = await ApiService.post("/auth/verify-recaptcha", { token });
    return result.data.success;
  } catch (error: unknown) {
    const { message } = handleError(error, "Erro com o Captcha.");
    toast.error(message);
    return false;
  }
};

const login = async (
  email: string,
  password: string,
  token: string
): Promise<TLogin | null> => {
  try {
    const res = await ApiService.post(`/auth/login`, {
      email,
      password,
      token
    });
    const { message, success, user } = res.data;
    if (success) {
      toast.success(message);
      return {
        status: true,
        user,
        message
      };
    } else {
      toast.info(message, {
        autoClose: false
      });
      return {
        status: false,
        user,
        message
      };
    }
  } catch (error: any) {
    const { message } = handleError(error, "Erro desconhecido ao logar.");
    toast.error(message);
    return null;
  }
};

const logout = async (): Promise<boolean> => {
  try {
    await ApiService.post("/auth/logout");
    return true;
  } catch (error: unknown) {
    handleError(error, "Erro ao tentar fazer logout:");
    return false;
  }
};

const currentUser = async (): Promise<TUser | null> => {
  try {
    const res = await ApiService.get("/auth/current-user");
    return res.data.user;
  } catch (error: unknown) {
    handleError(error, "Erro na requisição para obter o usuário atual:");
    return null;
  }
};

const verifyEmail = async (token: string): Promise<TVerifyEmail> => {
  try {
    const res = await ApiService.post(`/auth/verify-email/`, { token });
    return res.data;
  } catch (error: unknown) {
    return handleError(
      error,
      "Ocorreu um erro inesperado durante a verificação de e-mail."
    );
  }
};

const resetPassword = async (email: string): Promise<string | null> => {
  try {
    const { data } = await ApiService.post("/auth/send-reset-password-email", {
      email
    });
    return data.message;
  } catch (error) {
    handleError(error);
    return null;
  }
};
const changePassword = async (
  password: string,
  token: string
): Promise<TApiRes> => {
  try {
    const res = await ApiService.post(`/auth/change-password`, {
      password,
      token
    });
    return res.data;
  } catch (error: unknown) {
    return handleError(error);
  }
};

const verifyPasswordResetToken = async (token: string): Promise<TApiRes> => {
  try {
    const res = await ApiService.post("/auth/verify-password-reset-token", {
      token
    });
    return res.data;
  } catch (error: unknown) {
    return handleError(error);
  }
};

const emailExists = async (email: string): Promise<TEmailExists> => {
  try {
    const res = await ApiService.get("/auth/email-exists", { email });
    return res.data;
  } catch (error: unknown) {
    return handleError(error);
  }
};

const accountSetup = async (
  email: string,
  token: string,
  provider: string,
  setup: string
): Promise<TLoginSocial> => {
  try {
    const setupRes = await ApiService.post("/auth/account-setup", {
      email,
      token,
      provider,
      setup
    });
    return setupRes.data;
  } catch (error) {
    return handleError(error);
  }
};

const signUpSocial = async (
  fullName: string,
  email: string,
  type: string,
  token: string,
  doc: string
): Promise<TLoginSocial> => {
  try {
    const setupRes = await ApiService.post("/auth/signup-social", {
      fullName,
      email,
      type,
      token,
      phone: "12345678910",
      ...(type === "COMPANY" ? { cnpj: doc } : { cpf: doc })
    });
    return setupRes.data;
  } catch (error) {
    return handleError(error);
  }
};

export const LoginService = {
  signUp,
  login,
  logout,
  currentUser,
  verifyEmail,
  resetPassword,
  changePassword,
  verifyPasswordResetToken,
  googleVerifyReCaptcha,
  emailExists,
  accountSetup,
  signUpSocial
};
